import React, { Component } from "react";
import PropTypes from "prop-types";
import displayValuesFor from "./displayValuesFor";
import GenericChartComponent from "../GenericChartComponent";
import { formatNumber, shouldHideField } from "../../../utils/numberFormatting";

import ToolTipText from "./ToolTipText";
import ToolTipTSpanLabel from "./ToolTipTSpanLabel";
import { isDefined, identity, noop, functor } from "../utils";

// Canvas-based text measurement utility
let measureCanvas;
function getTextWidth(text, fontFamily = "Arial", fontSize = 12) {
  if (!measureCanvas) {
    measureCanvas = document.createElement("canvas");
  }
  const context = measureCanvas.getContext("2d");
  context.font = `${fontSize}px ${fontFamily}`;
  const metrics = context.measureText(text);
  return metrics.width;
}

// Constants
const TOOLTIP_BG_HEIGHT = 18;
const TOOLTIP_BG_PADDING = 6;

class SingleValueTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
  }
  renderSVG(moreProps) {
    const {
      onClick,
      fontFamily,
      fontSize,
      labelFill,
      valueFill,
      bgFill,
      className,
    } = this.props;
    const {
      xDisplayFormat,
      yDisplayFormat,
      xLabel,
      yLabel,
      xAccessor,
      yAccessor,
    } = this.props;
    const { displayValuesFor } = this.props;

    const {
      chartConfig: { width, height },
    } = moreProps;
    const currentItem = displayValuesFor(this.props, moreProps);

    const xDisplayValue =
      isDefined(currentItem) && isDefined(xAccessor(currentItem))
        ? xDisplayFormat(xAccessor(currentItem))
        : "n/a";
    const yDisplayValue =
      isDefined(currentItem) && isDefined(yAccessor(currentItem))
        ? yDisplayFormat(yAccessor(currentItem))
        : "n/a";

    // Build elements array, only including non-n/a fields
    const elements = [];

    // Add X field if available and not n/a
    if (xLabel && !shouldHideField(xDisplayValue)) {
      elements.push(
        <ToolTipTSpanLabel
          key="x-label"
          x={0}
          dy="5"
          fill={labelFill}
        >{`${xLabel}: `}</ToolTipTSpanLabel>,
      );
      elements.push(
        <tspan key="x-value" fill={valueFill}>{`${xDisplayValue} `}</tspan>,
      );
    }

    // Add Y field if available and not n/a
    if (!shouldHideField(yDisplayValue)) {
      elements.push(
        <ToolTipTSpanLabel
          key="y-label"
          fill={labelFill}
        >{`${yLabel}: `}</ToolTipTSpanLabel>,
      );
      elements.push(
        <tspan key="y-value" fill={valueFill}>
          {yDisplayValue}
        </tspan>,
      );
    }

    const { origin: originProp } = this.props;
    const origin = functor(originProp);
    const [x, y] = origin(width, height);

    // Don't render tooltip if no fields to show
    if (elements.length === 0) {
      return null;
    }

    // Calculate text width for background
    const fullText = `${xLabel ? xLabel + ": " + xDisplayValue + " " : ""}${yLabel}: ${yDisplayValue}`;
    const textWidth = getTextWidth(
      fullText,
      fontFamily || "Arial",
      fontSize || 12,
    );
    const bgWidth = textWidth + TOOLTIP_BG_PADDING * 2;

    return (
      <g
        className={className}
        transform={`translate(${x}, ${y})`}
        onClick={onClick}
        data-evo-id="tooltip-single-value"
      >
        {/* Background rectangle */}
        <rect
          x={-TOOLTIP_BG_PADDING}
          y={-5}
          width={bgWidth}
          height={TOOLTIP_BG_HEIGHT}
          fill={bgFill || "#1f2937"}
          rx={6}
          ry={6}
        />
        <ToolTipText x={0} y={0} fontFamily={fontFamily} fontSize={fontSize}>
          {elements}
        </ToolTipText>
      </g>
    );
  }
  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        drawOn={["mousemove"]}
      />
    );
  }
}

SingleValueTooltip.propTypes = {
  xDisplayFormat: PropTypes.func,
  yDisplayFormat: PropTypes.func.isRequired,
  xLabel: PropTypes.string,
  yLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  labelFill: PropTypes.string.isRequired,
  valueFill: PropTypes.string,
  bgFill: PropTypes.string,
  origin: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  className: PropTypes.string,
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
  onClick: PropTypes.func,
  displayValuesFor: PropTypes.func,
  xAccessor: PropTypes.func,
  yAccessor: PropTypes.func,
};

SingleValueTooltip.defaultProps = {
  origin: [0, 0],
  labelFill: "#4682B4",
  valueFill: "#000000",
  yDisplayFormat: (d) => formatNumber(d, 2),
  displayValuesFor: displayValuesFor,
  xAccessor: noop,
  yAccessor: identity,
  className: "react-stockcharts-tooltip",
};

export default SingleValueTooltip;
